import React from 'react';
import PlatformModules from '../components/PlatformModules';
import { Button } from '../components/ui/button';
import { ArrowRight, Check, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Platform = () => {
  const navigate = useNavigate();

  return (
    <div className="platform-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-6 mb-16">
            <div className="inline-block">
              <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Enterprise Platform
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
              Comprehensive Governance
              <br />
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Intelligence Platform
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Transform cultural complexity into systematic advantage with our hybrid AI-human platform, 
              delivering 90%+ accuracy across 47 jurisdictions through proven primary source networks.
            </p>
          </div>

          {/* Key Benefits Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            {[
              {
                title: "Primary Source Networks",
                description: "70-year established access to government officials and business leaders",
                image: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&q=80",
                benefits: ["Government Access", "Business Networks", "Historical Data", "Expert Validation"]
              },
              {
                title: "AI-Powered Analysis",
                description: "Advanced machine learning with cultural intelligence across all markets",
                image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80",
                benefits: ["Pattern Recognition", "Real-time Processing", "Predictive Analytics", "Risk Assessment"]
              },
              {
                title: "Expert Validation",
                description: "Human-in-loop quality assurance from regional specialists",
                image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80",
                benefits: ["Regional Experts", "Quality Assurance", "Cultural Context", "Compliance Check"]
              }
            ].map((benefit, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="h-48 overflow-hidden">
                  <img 
                    src={benefit.image} 
                    alt={benefit.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 space-y-4">
                  <h3 className="text-xl font-bold text-gray-900">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                  <ul className="space-y-2">
                    {benefit.benefits.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                        <Check className="w-4 h-4 text-green-600" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Modules */}
      <PlatformModules />

      {/* Technology Stack Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Built on Enterprise-Grade Technology
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Secure, scalable, and compliant infrastructure for global operations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Cloud Infrastructure", icon: "☁️", desc: "Multi-region deployment" },
              { title: "Data Security", icon: "🔒", desc: "End-to-end encryption" },
              { title: "API Integration", icon: "🔌", desc: "REST & GraphQL APIs" },
              { title: "24/7 Monitoring", icon: "📊", desc: "Real-time system health" }
            ].map((tech, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                <div className="text-4xl mb-3">{tech.icon}</div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">{tech.title}</h3>
                <p className="text-gray-600 text-sm">{tech.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-indigo-600">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl md:text-5xl font-bold text-white">
            Ready to Transform Your Global Operations?
          </h2>
          <p className="text-xl text-blue-100">
            Join leading enterprises leveraging our platform for systematic competitive advantage
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/contact')}
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg transition-all duration-200 hover:shadow-xl group"
            >
              Request Demo
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/about')}
              className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-6 text-lg transition-all duration-200"
            >
              Meet Our Team
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Platform;


